/**
 * Daily rate (F3-H): VES/USD rate history + capturing a new one
 * (manual or auto-BCV). The `exchange_rate` table is the source of truth for dual
 * billing; `org_settings`'s cache is updated once the latest rate lands.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Coins, RefreshCw } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useAddRate, useRates } from "../api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const FinanceRates: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const rates = useRates(activeOrgId);
  const addRate = useAddRate();
  const [value, setValue] = createSignal("");

  const submitManual = () => {
    const parsed = Number(value().replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast(t(msg`Enter a valid rate`), "error");
      return;
    }
    addRate.mutate(
      { org: activeOrgId(), vesPerUsd: parsed },
      {
        onSuccess: () => {
          setValue("");
          showToast(t(msg`Rate saved`), "success");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  const submitAuto = () =>
    addRate.mutate(
      { org: activeOrgId(), auto: true },
      {
        onSuccess: () => showToast(t(msg`BCV rate fetched`), "success"),
        onError: (e) => showToast(e.message, "error"),
      },
    );

  return (
    <div class="space-y-4">
      <div class="rounded-xl border border-border p-4">
        <h2 class="mb-3 flex items-center gap-2 font-semibold">
          <Coins class="h-5 w-5 text-primary" />
          {t(msg`Set today's rate (Bs / USD)`)}
        </h2>
        <div class="flex flex-wrap items-center gap-2">
          <input
            class={`${field} max-w-[10rem]`}
            inputmode="decimal"
            placeholder="36.50"
            value={value()}
            onInput={(e) => setValue(e.currentTarget.value)}
            data-testid="rate-input"
          />
          <Button onClick={submitManual} disabled={addRate.isPending} data-testid="rate-save">
            {t(msg`Save rate`)}
          </Button>
          <Button variant="secondary" onClick={submitAuto} disabled={addRate.isPending}>
            <RefreshCw class="mr-1 h-4 w-4" />
            {t(msg`Fetch BCV`)}
          </Button>
        </div>
      </div>

      <div class="rounded-xl border border-border p-4">
        <h3 class="mb-2 font-semibold">{t(msg`Rate history`)}</h3>
        <Show when={!rates.isLoading} fallback={<Skeleton class="h-24 w-full" />}>
          <Show
            when={(rates.data?.length ?? 0) > 0}
            fallback={<p class="text-sm text-muted-foreground">{t(msg`No rates yet`)}</p>}
          >
            <ul class="divide-y divide-border" data-testid="rate-history">
              <For each={rates.data}>
                {(r) => (
                  <li class="flex items-center justify-between py-2 text-sm">
                    <span class="tabular-nums">Bs. {r.vesPerUsd.toFixed(2)}</span>
                    <span class="text-muted-foreground">{r.rateDate}</span>
                    <span class="rounded-full bg-secondary/20 px-2 py-0.5 text-xs">
                      {r.source === "bcv_auto" ? "BCV" : t(msg`Manual`)}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </div>
  );
};
